/**
 * "Arrange script" APPLY (F-60, scoped to the F-28 Original Script column) —
 * pure doc logic over BoardDoc (no React, no DOM). The caller wraps this in
 * `session.batch` so the whole arrangement is ONE transaction = one sync unit
 * = one undo step (F-60.5).
 *
 * Per planned cell (a source cell whose text splits into n ≥ 2 pieces):
 *  - the ORIGINAL row keeps piece 1 in the source column; every other column
 *    of that row is UNTOUCHED (the user's requirement: row-mates stay with the
 *    original row);
 *  - n − 1 rows are inserted DIRECTLY BELOW, each carrying one subsequent
 *    piece in the source column ONLY (other columns empty — F-60.3 default);
 *  - comments/elements anchored to the original row stay with it (same rowId —
 *    F-60.6), and anything anchored to lower rows follows them down
 *    automatically (C1 ord keys);
 *  - F-60.7: mapped translation targets on the original row that hold content
 *    are flagged `st:"outdated"` — their source just changed.
 *
 * Concurrency guards (docs/07):
 *  - C6 stale-preview: each plan carries the text the preview was computed
 *    from; a cell that no longer matches is SKIPPED (reported, not applied) —
 *    never overwrite an edit someone made while the preview sat open.
 *  - C4: a row deleted while the preview sat open is skipped.
 *
 * Rows are processed bottom-most first, so each insertion index (read from one
 * initial order snapshot inside the transaction) stays valid — inserts land
 * strictly below every not-yet-processed row.
 */
import type { BoardDoc } from "@kola/shared";

/** One source cell's planned split, as confirmed in the preview dialog. */
export interface ArrangeCellPlan {
  rowId: string;
  /**
   * The final cell texts: `pieces[0]` replaces the original cell, the rest go
   * one-per-new-row below. Plans with fewer than 2 pieces are no-ops.
   */
  pieces: readonly string[];
  /** C6 guard: the cell text the preview was computed from. */
  sourceText: string;
}

export interface ArrangeResult {
  /** Cells actually split. */
  arranged: number;
  /** Rows inserted below them. */
  newRows: number;
  /** Plans skipped — cell changed since the preview, or its row was deleted. */
  skipped: number;
}

export function applyArrange(
  board: BoardDoc,
  sheetId: string,
  colId: string,
  plans: readonly ArrangeCellPlan[],
  mintRowId: () => string,
  origin?: unknown,
): ArrangeResult {
  return board.transact(() => {
    const result: ArrangeResult = { arranged: 0, newRows: 0, skipped: 0 };
    if (!board.hasColumn(sheetId, colId)) {
      result.skipped = plans.length;
      return result;
    }

    // One order snapshot; bottom-most plans first (see the module note).
    const rowIndex = new Map(board.sortedRowIds(sheetId).map((id, i) => [id, i]));
    const ordered = plans
      .filter((plan) => plan.pieces.length >= 2)
      .map((plan) => ({ plan, index: rowIndex.get(plan.rowId) }))
      .filter((entry): entry is { plan: ArrangeCellPlan; index: number } => {
        if (entry.index === undefined) {
          result.skipped++; // C4: row deleted while the preview sat open
          return false;
        }
        return true;
      })
      .sort((a, b) => b.index - a.index);

    // Mapped translation targets (F-60.7) — read once, they apply per row.
    const mappedTargets = board
      .sortedColIds(sheetId)
      .filter(
        (id) => id !== colId && board.getColumnTranslation(sheetId, id) !== undefined,
      );

    for (const { plan, index } of ordered) {
      // C6 stale-preview guard, re-checked INSIDE the transaction.
      if (board.getCellText(sheetId, plan.rowId, colId) !== plan.sourceText) {
        result.skipped++;
        continue;
      }

      // Original row keeps piece 1; other columns of the row are untouched.
      board.setCellValueAsHuman(sheetId, plan.rowId, colId, plan.pieces[0], origin);

      // The rest, one per fresh row directly below, source column only.
      const rest = plan.pieces.slice(1);
      const newIds = board.addRows(
        sheetId,
        rest.map(() => mintRowId()),
        { index: index + 1, origin },
      );
      newIds.forEach((rowId, i) => {
        board.setCellValueAsHuman(sheetId, rowId, colId, rest[i] as string, origin);
      });

      // F-60.7: the original row's translated targets just lost their source.
      for (const targetColId of mappedTargets) {
        const target = board.getCell(sheetId, plan.rowId, targetColId);
        if (target !== undefined && target.v !== null && target.v !== "") {
          board.setCellStatus(sheetId, plan.rowId, targetColId, "outdated", origin);
        }
      }

      result.arranged++;
      result.newRows += newIds.length;
    }
    return result;
  }, origin);
}
